import type { LineItem } from '@quote-tool/shared';
import type { CatalogPayload, CatalogProduct } from './api';

/**
 * Re-price the lines of an opened deal against a freshly refreshed catalog.
 *
 * A saved quote stores the prices that were current when it was printed. After
 * a catalog refresh those may have moved, and reprinting must not quietly go
 * out with figures the catalog no longer backs. This recomputes each catalog
 * line's base price and option prices from the current catalog, and reports
 * exactly what moved so the rep can be told rather than surprised.
 *
 * What it deliberately does NOT touch:
 *   - Custom lines, which have no catalog backing by definition.
 *   - Quantity, commitment, datacenter, or anything else the rep chose.
 *   - Lines whose product is gone from the catalog: those keep their stored
 *     price and are reported separately, because silently zeroing or dropping
 *     them would corrupt a quote the customer already holds.
 */

export interface PriceChange {
  lineId: string;
  name: string;
  field: 'base' | 'setup' | 'option';
  optionName?: string;
  before: number;
  after: number;
}

export interface RepriceResult {
  lines: LineItem[];
  changes: PriceChange[];
  /** Catalog lines whose product no longer exists; left untouched. */
  missing: Array<{ lineId: string; name: string }>;
}

function findProduct(line: LineItem, catalog: CatalogPayload): CatalogProduct | undefined {
  // planCode is the reliable key. Lines saved before it existed fall back to the
  // name, which is what the PDF shows and so is stable in practice.
  if (line.planCode) {
    const byCode = catalog.products.find((p) => p.planCode === line.planCode);
    if (byCode) return byCode;
  }
  return catalog.products.find((p) => p.name === line.name);
}

export function repriceLines(lines: LineItem[], catalog: CatalogPayload): RepriceResult {
  const changes: PriceChange[] = [];
  const missing: Array<{ lineId: string; name: string }> = [];

  const next = lines.map((line) => {
    if (line.isCustomLine) return line;

    const product = findProduct(line, catalog);
    if (!product) {
      missing.push({ lineId: line.id, name: line.name });
      return line;
    }

    let changed = false;
    const updated: LineItem = { ...line };

    if (product.monthlyPriceCad !== line.basePriceMonthly) {
      changes.push({
        lineId: line.id, name: line.name, field: 'base',
        before: line.basePriceMonthly, after: product.monthlyPriceCad,
      });
      updated.basePriceMonthly = product.monthlyPriceCad;
      changed = true;
    }

    // A line on commitment never carries a setup fee; leave those alone so
    // re-pricing cannot reintroduce one the commitment waived.
    if (line.commitMonths === 0 && product.setupFeeCad !== line.basePriceSetup) {
      changes.push({
        lineId: line.id, name: line.name, field: 'setup',
        before: line.basePriceSetup, after: product.setupFeeCad,
      });
      updated.basePriceSetup = product.setupFeeCad;
      changed = true;
    }

    const nextOptions = line.options.map((opt) => {
      const current = product.options.find((o) => o.optionCode === opt.id);
      // An option that vanished from the catalog keeps its stored price: the
      // customer was quoted it, and dropping it would change what was sold.
      if (!current || current.monthlyPriceCad === opt.monthlyPrice) return opt;
      changes.push({
        lineId: line.id, name: line.name, field: 'option', optionName: opt.name,
        before: opt.monthlyPrice, after: current.monthlyPriceCad,
      });
      changed = true;
      return {
        ...opt,
        monthlyPrice: current.monthlyPriceCad,
        appliesCommitmentDiscount: current.appliesCommitmentDiscount,
      };
    });

    if (!changed) return line;
    updated.options = nextOptions;
    // Backfill the planCode while we have the match, so a quote saved before it
    // existed becomes reliably re-priceable from here on.
    if (!updated.planCode) updated.planCode = product.planCode;
    return updated;
  });

  return { lines: next, changes, missing };
}

/** One-line human summary for the banner shown after a refresh. */
export function describeChanges(result: RepriceResult): string | null {
  const { changes, missing } = result;
  if (changes.length === 0 && missing.length === 0) return null;

  const parts: string[] = [];
  if (changes.length > 0) {
    parts.push(
      `${changes.length} price${changes.length === 1 ? '' : 's'} updated from the refreshed catalog`,
    );
  }
  if (missing.length > 0) {
    parts.push(
      `${missing.length} line${missing.length === 1 ? '' : 's'} no longer in the catalog ` +
      `(${missing.map((m) => m.name).join(', ')}) — kept at the stored price`,
    );
  }
  return parts.join('; ') + '.';
}
